import type { CSSProperties, JSX } from 'react';

interface SwatchProps {
  hex: string[];
  label: string;
  size?: number;
  selected?: boolean;
  disabled?: boolean;
}

/**
 * The swatch was designed before the palette was fixed, because it has to sit legibly on
 * whatever ground the page uses. `BIAŁY` is #F4F2EE — almost exactly the page ground —
 * which is why the ring is not optional.
 *
 * Three shapes, driven by the data: one hex is a solid fill, two is a diagonal split
 * (16 of the 45 colours), three is a multicolour band (WIELOKOLOROWY). An unmapped colour
 * arrives with an empty hex array and renders as a labelled chip, never as a wrong swatch.
 */
export function Swatch({ hex, label, size = 30, selected = false, disabled = false }: SwatchProps): JSX.Element {
  const style: CSSProperties = { width: size, height: size };

  /*
   * An unmapped colour still renders as a circle.
   *
   * It used to render as a text pill, so a rail of round swatches grew an oval in the
   * middle of it whenever one colour lacked a hex — a different shape, a different width,
   * and the row no longer scanned as a row of colours. The label is still the honest thing
   * we know, so it goes in the tooltip and, on the product page, in the caption beside the
   * axis; the swatch itself keeps the shape of its neighbours.
   */
  if (hex.length === 0) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full text-2xs font-semibold"
        style={{
          ...style,
          background:
            'repeating-linear-gradient(135deg, var(--color-ground-deep) 0 4px, var(--color-muted-soft) 4px 8px)',
          color: 'var(--color-ink-soft)',
          boxShadow: selected
            ? '0 0 0 2px var(--color-ground), 0 0 0 3px var(--color-brand)'
            : 'inset 0 0 0 1px var(--color-line)',
          opacity: disabled ? 0.35 : 1,
        }}
        title={label}
      >
        {size >= 28 ? '?' : ''}
      </span>
    );
  }

  const background =
    hex.length === 1
      ? hex[0]
      : hex.length === 2
        ? `linear-gradient(135deg, ${hex[0]} 0 49.5%, ${hex[1]} 50.5% 100%)`
        : `linear-gradient(135deg, ${hex[0]} 0 33%, ${hex[1]} 33% 66%, ${hex[2]} 66% 100%)`;

  return (
    <span
      className="inline-block rounded-full transition-transform duration-200"
      style={{
        ...style,
        background,
        // The ring is what keeps a near-ground colour visible.
        boxShadow: selected
          ? '0 0 0 2px var(--color-ground), 0 0 0 4px var(--color-brand)'
          : 'inset 0 0 0 1px rgb(34 38 43 / 0.18)',
        opacity: disabled ? 0.35 : 1,
        transform: selected ? 'scale(1.06)' : undefined,
      }}
      aria-hidden="true"
      title={label}
    />
  );
}


/** A rail of swatches under a product card: colour is how this catalogue is shopped. */
export function SwatchRail({
  colors,
  max = 6,
}: {
  colors: Array<{ slug: string; label: string; hex: string[] }>;
  max?: number;
}): JSX.Element | null {
  if (colors.length === 0) return null;
  const shown = colors.slice(0, max);
  const rest = colors.length - shown.length;
  return (
    <div className="flex items-center gap-1.5" aria-hidden="true">
      {shown.map((c) => (
        <Swatch key={c.slug} hex={c.hex} label={c.label} size={16} />
      ))}
      {rest > 0 && <span className="text-2xs text-[var(--color-muted)] font-semibold">+{rest}</span>}
    </div>
  );
}
